import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Copy, Plus, Trash2, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import type { SessionExercise, SessionRecord } from "@/hooks/useSessions";

interface DuplicateSessionDialogProps {
  session: SessionRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (title: string, exercises: SessionExercise[]) => void;
  loading: boolean;
}

export function DuplicateSessionDialog({
  session,
  open,
  onOpenChange,
  onConfirm,
  loading,
}: DuplicateSessionDialogProps) {
  const [title, setTitle] = useState("");
  const [exercises, setExercises] = useState<SessionExercise[]>([]);
  const [newExName, setNewExName] = useState("");
  const [newExSets, setNewExSets] = useState("");
  const [newExReps, setNewExReps] = useState("");

  // Sync state when session changes
  const [prevSessionId, setPrevSessionId] = useState<string | null>(null);
  if (session && session.id !== prevSessionId) {
    setPrevSessionId(session.id);
    setTitle(`${session.title} (copie)`);
    setExercises([...((session.exercises as SessionExercise[]) || [])]);
    setNewExName("");
    setNewExSets("");
    setNewExReps("");
  }

  const removeExercise = (index: number) => {
    setExercises((prev) => prev.filter((_, i) => i !== index));
  };

  const updateExercise = (index: number, field: keyof SessionExercise, value: string) => {
    setExercises((prev) =>
      prev.map((ex, i) => (i === index ? { ...ex, [field]: value || undefined } : ex))
    );
  };

  const moveExercise = (from: number, to: number) => {
    if (to < 0 || to >= exercises.length) return;
    setExercises((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(from, 1);
      updated.splice(to, 0, moved);
      return updated;
    });
  };

  const addExercise = () => {
    if (!newExName.trim()) return;
    setExercises((prev) => [
      ...prev,
      {
        name: newExName.trim(),
        sets: newExSets || undefined,
        reps: newExReps || undefined,
      },
    ]);
    setNewExName("");
    setNewExSets("");
    setNewExReps("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-sm">Dupliquer et modifier la séance</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 pr-1">
          {/* Title */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Nom de la copie</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Nom de la séance"
              className="text-sm"
              autoFocus
            />
          </div>

          {/* Exercises list */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">
              Exercices ({exercises.length})
            </label>
            {exercises.length === 0 ? (
              <p className="text-[10px] text-muted-foreground py-2 text-center">Aucun exercice</p>
            ) : (
              <div className="space-y-1">
                {exercises.map((ex, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-1.5 rounded-lg border border-border bg-card p-2 text-xs"
                  >
                    <span className="font-bold text-primary w-4 text-center shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0 space-y-1">
                      <Input
                        value={ex.name}
                        onChange={(e) => updateExercise(i, "name", e.target.value)}
                        className="text-xs h-7 font-medium"
                        placeholder="Nom"
                      />
                      <div className="flex gap-1.5">
                        <Input
                          value={ex.sets || ""}
                          onChange={(e) => updateExercise(i, "sets", e.target.value)}
                          className="text-[10px] h-6 w-16"
                          placeholder="Séries"
                        />
                        <Input
                          value={ex.reps || ""}
                          onChange={(e) => updateExercise(i, "reps", e.target.value)}
                          className="text-[10px] h-6 w-16"
                          placeholder="Reps"
                        />
                        <Input
                          value={ex.rest || ""}
                          onChange={(e) => updateExercise(i, "rest", e.target.value)}
                          className="text-[10px] h-6 w-16"
                          placeholder="Repos"
                        />
                      </div>
                      <Input
                        value={ex.notes || ""}
                        onChange={(e) => updateExercise(i, "notes", e.target.value)}
                        className="text-[10px] h-6"
                        placeholder="Notes (ex : tempo 3-1-2, unilatéral…)"
                      />
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        disabled={i === 0}
                        onClick={() => moveExercise(i, i - 1)}
                      >
                        <ArrowUp className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        disabled={i === exercises.length - 1}
                        onClick={() => moveExercise(i, i + 1)}
                      >
                        <ArrowDown className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                        onClick={() => removeExercise(i)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add exercise */}
            <div className="rounded-lg border border-dashed border-primary/20 bg-primary/5 p-2.5 space-y-2">
              <p className="text-[10px] font-medium text-muted-foreground">Ajouter un exercice</p>
              <div className="flex items-end gap-1.5">
                <div className="flex-1 space-y-1">
                  <Input
                    value={newExName}
                    onChange={(e) => setNewExName(e.target.value)}
                    placeholder="Nom de l'exercice"
                    className="text-xs h-8"
                    onKeyDown={(e) => e.key === "Enter" && addExercise()}
                  />
                </div>
                <div className="w-16 space-y-1">
                  <Input
                    value={newExSets}
                    onChange={(e) => setNewExSets(e.target.value)}
                    placeholder="Séries"
                    className="text-xs h-8"
                  />
                </div>
                <div className="w-16 space-y-1">
                  <Input
                    value={newExReps}
                    onChange={(e) => setNewExReps(e.target.value)}
                    placeholder="Reps"
                    className="text-xs h-8"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 w-8 p-0 shrink-0"
                  disabled={!newExName.trim()}
                  onClick={addExercise}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            size="sm"
            disabled={!title.trim() || loading}
            onClick={() => onConfirm(title.trim(), exercises)}
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
            Dupliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
